import { Link } from 'react-router';
import { ArrowUpRight, CalendarPlus, CircleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/shared/page-header';
import { EmptyState, ErrorState, LoadingState } from '@/components/shared/states';
import { StatusBadge } from '@/components/shared/status-badge';
import { fmtDate, fmtTime, money } from '@/lib/format';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { onboardingStateKey, useDashboard, useOnboardingState, type Dashboard } from './use-dashboard';

/** Faixa de indicadores separados por divisórias — uma linha de "livro-caixa", não uma grade de cards. */
function Ledger({ items }: { items: { label: string; value: string; hint?: string; tone?: 'warn' }[] }) {
  return (
    <dl className="grid grid-cols-2 overflow-hidden rounded-lg border bg-card sm:grid-cols-3 lg:grid-cols-[repeat(auto-fit,minmax(9rem,1fr))]">
      {items.map((i) => (
        <div key={i.label} className="border-b border-r px-4 py-3.5 last:border-r-0 sm:border-b-0">
          <dt className="text-xs text-muted-foreground">{i.label}</dt>
          <dd
            className={cn(
              'mt-1 font-display text-3xl leading-none font-bold tabular',
              i.tone === 'warn' && 'text-warning',
            )}
          >
            {i.value}
          </dd>
          {i.hint && <p className="mt-1 text-xs text-muted-foreground">{i.hint}</p>}
        </div>
      ))}
    </dl>
  );
}

function OnboardingBanner({ d }: { d: Dashboard }) {
  const o = d.onboarding;
  const state = useOnboardingState();
  const qc = useQueryClient();
  const dismiss = useMutation({
    mutationFn: () => api.patch('/onboarding', { dismissed: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: onboardingStateKey }),
  });
  const done = [o.hasBranch, o.hasHours, o.hasServices, o.hasBarbers, o.sitePublished].filter(Boolean).length;
  if (done === 5 || state.data?.dismissed) return null;
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-brass/40 bg-brass/10 p-4 sm:flex-row sm:items-center">
      <div className="flex-1">
        <p className="font-medium">Faltam {5 - done} de 5 passos para começar a receber agendamentos.</p>
        <p className="text-sm text-muted-foreground">Unidade, horário, serviços, equipe e site publicado.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button asChild>
          <Link to="/app/primeiros-passos">Continuar configuração</Link>
        </Button>
        <Button variant="ghost" onClick={() => dismiss.mutate()} disabled={dismiss.isPending}>
          Dispensar
        </Button>
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { data, isLoading, error, refetch } = useDashboard();
  const { me, can } = useSession();
  if (isLoading) return <LoadingState />;
  if (error || !data) return <ErrorState error={error} onRetry={refetch} />;

  const tz = data.timezone;
  const a = data.agenda;
  const active = (a.counts.requested ?? 0) + (a.counts.confirmed ?? 0) + (a.counts.in_progress ?? 0);
  const ledger: { label: string; value: string; hint?: string; tone?: 'warn' }[] = [
    { label: 'Atendimentos hoje', value: String(a.total), hint: `${a.counts.completed ?? 0} concluídos` },
    {
      label: 'A atender',
      value: String(active),
      hint: a.counts.requested ? `${a.counts.requested} aguardando confirmação` : undefined,
    },
    {
      label: 'Ocupação',
      value: a.occupancy == null ? '—' : `${Math.round(a.occupancy * 100)}%`,
      hint:
        a.occupancy == null
          ? 'Defina o horário de funcionamento'
          : `${Math.round(a.bookedMinutes / 60)}h de ${Math.round(a.openMinutes / 60)}h`,
    },
  ];
  if (data.finance) {
    ledger.push(
      {
        label: 'Faturado hoje',
        value: money(data.finance.today.revenueCents),
        hint: `Recebido ${money(data.finance.today.receivedCents)}`,
      },
      {
        label: 'A receber hoje',
        value: money(data.finance.today.pendingCents),
        tone: data.finance.today.pendingCents > 0 ? 'warn' : undefined,
        hint: 'Concluídos sem pagamento',
      },
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6">
      <PageHeader
        title={`Olá, ${me?.user.name.split(' ')[0] ?? ''}`}
        description={fmtDate(`${data.date}T12:00:00Z`, 'UTC', "EEEE, d 'de' MMMM")}
        actions={
          can('agenda.write') && (
            <Button asChild>
              <Link to="/app/agenda?novo=1">
                <CalendarPlus className="size-4" /> Novo agendamento
              </Link>
            </Button>
          )
        }
      />
      <OnboardingBanner d={data} />
      <Ledger items={ledger} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <section aria-labelledby="proximos" className="rounded-lg border bg-card">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h2 id="proximos" className="font-semibold">
              Próximos atendimentos
            </h2>
            <Link
              to="/app/agenda"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              Abrir agenda <ArrowUpRight className="size-3.5" />
            </Link>
          </div>
          {a.upcoming.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title="Nenhum atendimento pela frente hoje"
                description="Os agendamentos feitos pelo site e pela recepção aparecem aqui."
                image="/brand/empty-agenda.webp"
              />
            </div>
          ) : (
            <ol className="divide-y">
              {a.upcoming.map((u) => (
                <li key={u.id} className="flex items-center gap-4 px-4 py-3">
                  <span className="w-12 shrink-0 font-display text-xl font-bold tabular">
                    {fmtTime(u.startsAt, tz)}
                  </span>
                  <span
                    className="h-9 w-1 shrink-0 rounded-full"
                    style={{ background: u.barberColor }}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{u.customerName}</p>
                    <p className="truncate text-sm text-muted-foreground">
                      {u.serviceName} com {u.barberName}
                    </p>
                  </div>
                  <StatusBadge status={u.status} />
                </li>
              ))}
            </ol>
          )}
        </section>

        <div className="grid content-start gap-6">
          {data.finance && (
            <section aria-labelledby="mes" className="rounded-lg border bg-card">
              <div className="border-b px-4 py-3">
                <h2 id="mes" className="font-semibold">
                  Mês até agora
                </h2>
              </div>
              <dl className="grid grid-cols-3 gap-2 border-b px-4 py-3 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">Faturado</dt>
                  <dd className="tabular font-medium">{money(data.finance.month.revenueCents)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Recebido</dt>
                  <dd className="tabular font-medium">{money(data.finance.month.receivedCents)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Ticket médio</dt>
                  <dd className="tabular font-medium">{money(data.finance.month.averageTicketCents)}</dd>
                </div>
              </dl>
              <ul className="grid gap-3 p-4">
                {data.finance.barbers.map((b) => {
                  const pct = b.goalCents ? Math.min(1, b.revenueCents / b.goalCents) : null;
                  return (
                    <li key={b.id} className="grid gap-1">
                      <div className="flex items-baseline justify-between gap-2 text-sm">
                        <span className="font-medium">{b.name}</span>
                        <span className="tabular text-muted-foreground">
                          {money(b.revenueCents)}
                          {b.goalCents ? ` de ${money(b.goalCents)}` : ''}
                        </span>
                      </div>
                      <div
                        className="h-1.5 overflow-hidden rounded-full bg-muted"
                        role={pct == null ? undefined : 'meter'}
                        aria-valuenow={pct == null ? undefined : Math.round(pct * 100)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`Meta de ${b.name}`}
                      >
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${Math.round((pct ?? 0) * 100)}%`, background: b.color }}
                        />
                      </div>
                    </li>
                  );
                })}
                {data.finance.barbers.length === 0 && (
                  <p className="text-sm text-muted-foreground">Cadastre a equipe para acompanhar metas.</p>
                )}
              </ul>
            </section>
          )}

          <section aria-label="Avisos" className="grid gap-2">
            {data.finance && data.finance.openCashRegisters.length === 0 && can('cash.operate') && (
              <Link
                to="/app/caixa"
                className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3 text-sm hover:bg-accent"
              >
                <CircleAlert className="size-4 text-warning" /> Nenhum caixa aberto. Abra o caixa para
                registrar recebimentos.
              </Link>
            )}
            {data.customers && data.customers.inactive45 > 0 && (
              <Link
                to="/app/clientes?inactiveDays=45"
                className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3 text-sm hover:bg-accent"
              >
                <CircleAlert className="size-4 text-muted-foreground" /> {data.customers.inactive45} clientes
                não voltam há mais de 45 dias.
              </Link>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
